import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useInjuryToken } from '../../../hooks/useInjuryTokenStatus';
import type { InjuryTokenEligibility } from '../../../types/tokens';
import { UtSheet } from './UtSheet';

interface Props {
  open: boolean;
  onClose: () => void;
  playerId: string;
  /** The completed game the injury happened in. */
  gameId: string;
  matchNumber?: number | string;
  /** From useInjuryTokenEligibility for this player and game. */
  eligibility: InjuryTokenEligibility;
  onClaimed?: () => void;
}

const COMPARE: [string, string, string][] = [
  ['Use for', 'Planned absences', 'WNF injuries'],
  ['When you return', 'Higher, eases down', 'Half, builds up'],
  ['Best for', '1–2 week breaks', '3+ weeks out'],
  ['Cost', 'One earned token', 'Free'],
];

/**
 * Claim an injury token for a game the player was hurt in. activate_injury_token
 * applies it straight away (fair use; admins can deny it afterwards), records
 * the optional note, and swaps an active shield for the injury token.
 */
export const UtInjurySheet: React.FC<Props> = ({ open, onClose, playerId, gameId, matchNumber, eligibility, onClaimed }) => {
  const [notes, setNotes] = useState('');
  const { activateInjuryToken, isProcessing } = useInjuryToken();
  const streak = eligibility.effectiveStreak;
  const back = eligibility.returnStreak;

  useEffect(() => {
    if (open) setNotes('');
  }, [open]);

  const claim = async () => {
    try {
      const r = await activateInjuryToken(playerId, gameId, notes.trim() || undefined);
      toast.success(`Injury token claimed. You'll be back on a ${r.returnStreak}-game streak.`, { duration: 5000 });
      onClaimed?.();
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to claim injury token');
    }
  };

  return (
    <UtSheet
      open={open}
      onClose={onClose}
      busy={isProcessing}
      eyebrow={matchNumber ? `Match #${matchNumber} · Fair use` : 'Fair use'}
      eyebrowClassName="text-[#f0a35a]"
      title="Claim injury token"
      main={{
        label: isProcessing ? 'CLAIMING…' : 'CLAIM INJURY TOKEN',
        onClick: claim,
        disabled: !eligibility.eligible,
        className: 'bg-[#f0a35a] text-[#1a0c02]',
      }}
    >
      <div className="rounded-[10px] border border-ut-acc bg-ut-acc-soft px-3 py-[11px] text-[13px] leading-[1.45]">
        <b>Fair use.</b> Only for injuries picked up during a WNF game. It applies straight away, and admins can remove a claim that
        isn&apos;t one.
      </div>

      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2.5 rounded-xl bg-ut-card p-4">
        <div className="flex flex-col items-center gap-0.5">
          <span className="font-ut-display text-5xl font-extrabold leading-none">{streak}</span>
          <span className="text-xs text-ut-mut">Streak now</span>
        </div>
        <span className="font-ut-display text-2xl font-extrabold text-ut-mut">→</span>
        <div className="flex flex-col items-center gap-0.5">
          <span className="font-ut-display text-5xl font-extrabold leading-none text-[#f0a35a]">{back}</span>
          <span className="text-center text-xs text-ut-mut">Streak when you return</span>
        </div>
      </div>
      <span className="text-[13px] leading-[1.45] text-ut-mut">
        You&apos;re marked as injured and can miss as many games as you need. When you&apos;re back you start from {back} (half, rounded up)
        and build up.
        {eligibility.hasActiveShield ? ' Your active shield is replaced: the half is taken from your shield-protected streak.' : ''}
      </span>

      <div className="flex flex-col overflow-hidden rounded-xl bg-ut-card">
        <div className="grid grid-cols-3 gap-2 border-b border-ut-line px-3.5 py-2.5 font-ut-display text-[15px] font-extrabold">
          <span className="text-ut-mut">SHIELD OR INJURY?</span>
          <span className="text-ut-sky">SHIELD</span>
          <span className="text-[#f0a35a]">INJURY</span>
        </div>
        {COMPARE.map(([k, a, b]) => (
          <div key={k} className="grid grid-cols-3 gap-2 border-b border-ut-line px-3.5 py-[9px] text-[13px] last:border-b-0">
            <span className="text-ut-mut">{k}</span>
            <span>{a}</span>
            <span>{b}</span>
          </div>
        ))}
      </div>

      <label className="flex flex-col gap-[5px] text-[11px] font-semibold uppercase tracking-[0.1em] text-ut-mut">
        What happened? · optional
        <textarea
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g. Twisted ankle in the second half"
          className="resize-y rounded-[9px] border border-ut-line bg-ut-field px-3 py-2.5 font-ut-body text-sm font-medium normal-case tracking-normal text-ut-ink outline-none focus:border-ut-acc"
        />
      </label>
    </UtSheet>
  );
};
